/**
 * Instagram Service (Frontend)
 *
 * Handles Instagram URL detection and formatting.
 * Actual scraping is proxied through the backend (/api/instagram/scrape).
 *
 * SECURITY: Apify API token lives server-side only.
 */

import { apiPost } from './apiClient';

// ============================================================================
// TYPES
// ============================================================================

export interface InstagramPostData {
  type: 'Sidecar' | 'Video' | 'Image';
  caption: string;
  images: string[];
  videoUrl?: string;
  ownerUsername: string;
  ownerFullName: string;
  childPosts?: Array<{ alt: string; displayUrl: string }>;
}

export interface InstagramProfileData {
  fullName: string;
  username: string;
  biography: string;
  profilePicUrl: string;
  profilePicUrlHD: string;
  avatarBase64: string | null;
  followersCount: number;
  followsCount: number;
  postsCount: number;
  verified: boolean;
  isBusinessAccount: boolean;
  externalUrl: string;
}

// ============================================================================
// URL DETECTION (client-side)
// ============================================================================

export const INSTAGRAM_URL_REGEX = /(?:https?:\/\/)?(?:www\.)?instagram\.com\/(?:p|reel|reels)\/[a-zA-Z0-9_-]+/g;

export const extractInstagramUrls = (text: string): string[] => {
  const matches = text.match(INSTAGRAM_URL_REGEX);
  return matches || [];
};

export const normalizeInstagramUrl = (url: string): string => {
  if (!url.startsWith('http')) return `https://${url}`;
  return url;
};

// ============================================================================
// SCRAPING (via Backend)
// ============================================================================

/**
 * Scrape Instagram post/reel data through the backend.
 */
export const scrapeInstagramPost = async (url: string): Promise<InstagramPostData> => {
  const normalizedUrl = normalizeInstagramUrl(url);

  const response = await apiPost<{
    content: string;
    type: string;
    caption: string;
    ownerUsername: string;
    ownerFullName: string;
    childPosts: Array<{ alt?: string; displayUrl?: string }>;
    mediaUrls: string[];
    mediaData: Array<{ base64: string; mimeType: string }>;
  }>('/api/instagram/scrape', {
    url: normalizedUrl,
    downloadMedia: true,
  });

  return {
    type: (response.type as InstagramPostData['type']) || 'Image',
    caption: response.caption || '',
    images: response.mediaUrls || [],
    ownerUsername: response.ownerUsername || 'unknown',
    ownerFullName: response.ownerFullName || '',
    childPosts: (response.childPosts || []).map((post) => ({
      alt: post.alt || '',
      displayUrl: post.displayUrl || '',
    })),
  };
};

// ============================================================================
// PROFILE SCRAPING (via Backend)
// ============================================================================

/**
 * Fetch Instagram profile data by username through the backend.
 * Returns profile info including avatar as base64 (CORS-safe).
 */
export const fetchInstagramProfile = async (username: string): Promise<InstagramProfileData> => {
  const cleanUsername = username.replace(/^@/, '').trim();

  return apiPost<InstagramProfileData>('/api/instagram/profile', {
    username: cleanUsername,
  });
};

// ============================================================================
// MEDIA DOWNLOAD (kept for backwards compatibility)
// ============================================================================

export const downloadMediaAsBlob = async (url: string): Promise<Blob> => {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`Failed to download media: ${response.status}`);
  return await response.blob();
};

// ============================================================================
// TEXT FORMATTING
// ============================================================================

export const formatInstagramContentForAI = (data: InstagramPostData): string => {
  let content = `Instagram ${data.type} by @${data.ownerUsername}`;
  if (data.ownerFullName) content += ` (${data.ownerFullName})`;
  content += `:\n\n`;
  if (data.caption) content += `Caption:\n${data.caption}\n\n`;
  if (data.childPosts?.length) {
    const slidesWithAlt = data.childPosts.filter(post => post.alt);
    if (slidesWithAlt.length > 0) {
      content += `Slide contents (text from images):\n`;
      slidesWithAlt.forEach((post, i) => {
        content += `Slide ${i + 1}: ${post.alt}\n`;
      });
    }
  }
  return content;
};
